import { useState, useEffect } from 'react';
import { LoadingButton } from '../ui/LoadingSpinner';
import { handleApiError } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/currencyUtils';
import OmButton from '../ui/OmButton';
import OmTextField from '../ui/OmTextField';
import OmAlert from '../ui/OmAlert';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';

const ApprovalModal = ({ 
  show, 
  onClose, 
  onApprove, 
  isApproving,
  approvalDecision,
  setApprovalDecision,
  approvalNotes,
  setApprovalNotes,
  claim
}) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset error and success when modal opens/closes
  useEffect(() => {
    if (show) {
      setError(null);
      setSuccess(false);
    }
  }, [show]);

  // Reset error when form changes
  useEffect(() => {
    if (error) setError(null);
  }, [approvalDecision, approvalNotes, error]);

  if (!show) return null;

  const handleApprove = async () => {
    if (!approvalDecision) {
      setError('Please select a decision (Approve or Reject)');
      return;
    }

    if (!approvalNotes || approvalNotes.trim().length < 10) {
      setError(`Please provide detailed ${approvalDecision === 'APPROVE' ? 'approval notes' : 'rejection reason'} (minimum 10 characters)`);
      return;
    }

    try {
      await onApprove();
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Claim Approval</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isApproving}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {error && (
            <OmAlert
              alertType="error"
              message={error}
              showActions={false}
            />
          )}

          {success && (
            <OmAlert
              alertType="success"
              message="Approval decision processed successfully!"
              showActions={false}
            />
          )}

          {/* Claim Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Claim #{claim?.id}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Claimant:</span>
                <div className="font-medium text-gray-900">{claim?.claimantName}</div>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>
                <div className="font-medium text-gray-900">{claim?.type?.replace('_', ' ')}</div>
              </div>
              <div>
                <span className="text-gray-500">Amount:</span>
                <div className="font-medium text-gray-900">
                  {claim?.approvedBenefitAmount !== null && claim?.approvedBenefitAmount !== undefined 
                    ? formatCurrency(claim.approvedBenefitAmount)
                    : formatCurrency(claim?.benefitAmount || 0)
                  }
                </div>
              </div>
            </div>
            {claim?.recommendationNotes && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <span className="text-gray-500 text-sm">Officer's Recommendation:</span>
                <p className="text-gray-900 text-sm mt-1">{claim.recommendationNotes}</p>
              </div>
            )}
          </div>

          {/* Decision Selection */}
          <div>
            <OmSelect
              label="Your Decision *"
              value={approvalDecision || ''}
              onChange={(e) => setApprovalDecision(e.target.value)}
              disabled={isApproving || success}
              required
            >
              <OmSelectOption value="">-- Select Decision --</OmSelectOption>
              <OmSelectOption value="APPROVE">✓ Approve Claim</OmSelectOption>
              <OmSelectOption value="REJECT">✗ Reject Claim</OmSelectOption>
            </OmSelect>
            {approvalDecision && (
              <div className={`mt-2 text-sm flex items-center ${
                approvalDecision === 'APPROVE' ? 'text-green-600' : 'text-red-600'
              }`}>
                {approvalDecision === 'APPROVE' ? (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Claim will be approved
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Claim will be rejected
                  </>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <OmTextField
              label={approvalDecision === 'APPROVE' ? 'Approval Notes *' : approvalDecision === 'REJECT' ? 'Rejection Reason *' : 'Notes *'}
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              rows={3}
              placeholder={
                approvalDecision === 'APPROVE' 
                  ? 'Explain why you are approving this claim...' 
                  : approvalDecision === 'REJECT'
                  ? 'Explain why you are rejecting this claim...'
                  : 'Explain your decision...'
              }
              disabled={isApproving || success}
              required
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <OmButton
            type="button"
            onClick={onClose}
            buttonType="text-button"
            disabled={isApproving || success}
          >
            Cancel
          </OmButton>
          
          <OmButton
            type="submit"
            onClick={handleApprove}
            buttonType="filled-button"
            disabled={isApproving || success || !approvalDecision || !approvalNotes?.trim()}
          >
            {isApproving ? 'Processing...' : `${approvalDecision === 'APPROVE' ? 'Approve' : 'Reject'} Claim`}
          </OmButton>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;
